import { Badge } from '@shared/components/ui/badge';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { usePermissions } from '@shared/hooks/usePermissions';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getWithdrawalStatusBadgeConfig } from '../../config/withdrawalStatusDisplay';
import type { Withdrawal } from '../../models';

export interface ViewWithdrawalModalRef {
  open: (withdrawal: Withdrawal) => void;
  close: () => void;
}

interface ViewWithdrawalModalProps {
  onClose?: () => void;
}

const formatCurrency = (value?: number | null, currency?: string) => {
  if (value === undefined || value === null) return '—';
  return formatCurrencyAmount(value, currency || 'EUR');
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('es-ES', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(date);
};

const getWithdrawalWalletBalance = (withdrawal: Withdrawal): number | null => {
  if (withdrawal.wallet_balance != null) return withdrawal.wallet_balance;
  if (withdrawal.wallet?.balance_total != null) return withdrawal.wallet.balance_total;
  return null;
};

const getStatusBadge = (status: string, t: ReturnType<typeof useTranslation>['t']) => {
  const config = getWithdrawalStatusBadgeConfig(status, t);
  return (
    <Badge variant={config.variant} className={config.className}>
      {config.label}
    </Badge>
  );
};

export const ViewWithdrawalModal = forwardRef<ViewWithdrawalModalRef, ViewWithdrawalModalProps>(({ onClose }, ref) => {
  const { t } = useTranslation('withdrawalsModule');
  const { getTeamType } = usePermissions();
  const currency = useCurrency();
  const teamType = getTeamType();
  const is3pl = teamType === 'tenant_3pl';
  const modalRef = useRef<ModalCenterRef>(null);
  const [withdrawal, setWithdrawal] = useState<Withdrawal | null>(null);

  const openWithWithdrawal = useCallback((w: Withdrawal) => {
    setWithdrawal(w);
    // Pequeño delay para asegurar que el estado se actualice antes de abrir
    setTimeout(() => {
      modalRef.current?.openModal();
    }, 0);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      open: openWithWithdrawal,
      close: () => modalRef.current?.closeModal(),
    }),
    [openWithWithdrawal],
  );

  const handleClose = useCallback(() => {
    setWithdrawal(null);
    onClose?.();
  }, [onClose]);

  if (!withdrawal) return null;

  return (
    <ModalCenter
      ref={modalRef}
      title={`${t('actions.view')} - #${withdrawal.id}`}
      type="default"
      width="lg"
      onCloseModal={handleClose}
    >
      <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
        {/* Estado */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{t('table.columns.status')}:</span>
          {getStatusBadge(withdrawal.status, t)}
        </div>

        {/* Información principal */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Info
            label={t('table.columns.wallet')}
            value={
              withdrawal.wallet?.team?.name
                ? `${withdrawal.wallet.team.name} (#${withdrawal.wallet_id})`
                : `#${withdrawal.wallet_id}`
            }
          />
          <Info
            label={t('table.columns.wallet_balance')}
            value={formatCurrency(getWithdrawalWalletBalance(withdrawal), currency)}
          />
          {withdrawal.bank_account ? (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">{t('table.columns.bank_account')}</p>
              <div className="text-sm font-medium space-y-1">
                <p>{withdrawal.bank_account.holder_name}</p>
                <p className="text-muted-foreground font-normal">IBAN: {withdrawal.bank_account.iban}</p>
                {withdrawal.bank_account.bic && (
                  <p className="text-muted-foreground font-normal text-xs">BIC: {withdrawal.bank_account.bic}</p>
                )}
              </div>
            </div>
          ) : (
            <Info label={t('table.columns.bank_account')} value={`#${withdrawal.bank_account_id}`} />
          )}
          <Info
            label={t('table.columns.requested_amount')}
            value={formatCurrency(withdrawal.requested_amount, currency)}
          />
          <Info
            label={t('table.columns.approved_amount')}
            value={formatCurrency(withdrawal.approved_amount, currency)}
          />
          {withdrawal.bank_transfer_date && (
            <Info label={t('table.columns.bank_transfer_date')} value={formatDate(withdrawal.bank_transfer_date)} />
          )}
          {withdrawal.created_at && (
            <Info label={t('modals.view.requestDate')} value={formatDate(withdrawal.created_at)} />
          )}
        </div>

        {/* Detalles de cuenta bancaria */}
        {withdrawal.bank_account && (
          <div className="border-t pt-4 space-y-3">
            <h3 className="text-sm font-semibold text-foreground mb-3">{t('modals.view.bankAccountDetails')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Info label={t('modals.view.holder')} value={withdrawal.bank_account.holder_name} />
              <Info label="IBAN" value={withdrawal.bank_account.iban} />
              {withdrawal.bank_account.bic && <Info label="BIC" value={withdrawal.bank_account.bic} />}
              {withdrawal.bank_account.bank_name && (
                <Info label={t('modals.view.bank')} value={withdrawal.bank_account.bank_name} />
              )}
              {withdrawal.bank_account.country_code && (
                <Info label={t('modals.view.country')} value={withdrawal.bank_account.country_code} />
              )}
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('modals.view.status')}</p>
                <Badge
                  variant={
                    withdrawal.bank_account.status === 'VERIFIED'
                      ? 'transparent-green'
                      : withdrawal.bank_account.status === 'REJECTED'
                        ? 'destructive'
                        : withdrawal.bank_account.status === 'DISABLED'
                          ? 'secondary'
                          : 'transparent-orange'
                  }
                >
                  {withdrawal.bank_account.status === 'VERIFIED'
                    ? t('status.verified')
                    : withdrawal.bank_account.status === 'REJECTED'
                      ? t('status.rejected')
                      : withdrawal.bank_account.status === 'DISABLED'
                        ? t('status.disabled')
                        : t('status.pending')}
                </Badge>
              </div>
            </div>
          </div>
        )}

        {/* Usuarios */}
        <div className="border-t pt-4 space-y-3">
          <h3 className="text-sm font-semibold text-foreground mb-3">{t('modals.view.usersInvolved')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Info
              label={t('table.columns.requested_by')}
              value={
                withdrawal.requested_by_user?.name
                  ? `${withdrawal.requested_by_user.name} (#${withdrawal.requested_by_user_id})`
                  : withdrawal.requested_by_user_id
                    ? `#${withdrawal.requested_by_user_id}`
                    : '—'
              }
            />
            {is3pl && (
              <>
                <Info
                  label={t('table.columns.reviewed_by')}
                  value={
                    withdrawal.reviewed_by_user?.name
                      ? `${withdrawal.reviewed_by_user.name} (#${withdrawal.reviewed_by_user_id})`
                      : withdrawal.reviewed_by_user_id
                        ? `#${withdrawal.reviewed_by_user_id}`
                        : '—'
                  }
                />
                <Info
                  label={t('table.columns.processed_by')}
                  value={
                    withdrawal.processed_by_user?.name
                      ? `${withdrawal.processed_by_user.name} (#${withdrawal.processed_by_user_id})`
                      : withdrawal.processed_by_user_id
                        ? `#${withdrawal.processed_by_user_id}`
                        : '—'
                  }
                />
              </>
            )}
          </div>
        </div>

        {/* Notas y razones */}
        {(withdrawal.internal_note || withdrawal.rejection_reason) && (
          <div className="border-t pt-4 space-y-3">
            {withdrawal.internal_note && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('modals.create.fields.internal_note')}</p>
                <p className="text-sm font-medium bg-muted/50 p-3 rounded-md">{withdrawal.internal_note}</p>
              </div>
            )}
            {withdrawal.rejection_reason && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('modals.view.rejectionReason')}</p>
                <p className="text-sm font-medium bg-destructive/10 text-destructive p-3 rounded-md">
                  {withdrawal.rejection_reason}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </ModalCenter>
  );
});

ViewWithdrawalModal.displayName = 'ViewWithdrawalModal';

const Info = ({ label, value }: { label: string; value: string }) => (
  <div className="space-y-1">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-sm font-medium">{value}</p>
  </div>
);
